import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import ChecklistItem from 'src/components/CheckListItem/CheckListItem';
import {
  CHECKLIST_ITEM_STATE_ACTIVE,
  CHECKLIST_ITEM_STATE_COMPLETE,
  CHECKLIST_ITEM_STATE_INCOMPLETE,
} from 'src/components/CheckListItem/constants';
import buildLocalizedDateRangeLabel from 'src/util/time/dateRange';

import {
  PORTFOLIO_SHOW_LOADING_STATE_COMPLETE,
  PORTFOLIO_SHOW_LOADING_STATE_ERROR,
  PORTFOLIO_SHOW_LOADING_STATE_IDLE,
  PORTFOLIO_SHOW_LOADING_STATE_LOADING_CONSUMED,
  PORTFOLIO_SHOW_LOADING_STATE_LOADING_GENERATED,
  PORTFOLIO_SHOW_LOADING_STATE_LOADING_METERS,
  PORTFOLIO_SHOW_LOADING_STATE_LOADING_PORTFOLIO,
  PORTFOLIO_SHOW_LOADING_STATE_LOADING_RULES,
  PORTFOLIO_SHOW_LOADING_STATE_LOADING_TRADE_SUMMARY,
  PORTFOLIO_SHOW_LOADING_STATE_METERS_LOADED,
  PORTFOLIO_SHOW_LOADING_STATE_PREPARING_DATA,
  PORTFOLIO_SHOW_LOADING_STATE_RENDERING,
  PORTFOLIO_SHOW_LOADING_STATE_RULES_LOADED,
  PortfolioShowLoadingStates,
  STAGE_PROGRESS,
  StageKeys,
} from '../reducer/constants';

// Define constants for checklist stage ordering
export const CHECKLIST_STATES = {
  PORTFOLIO: 1,
  METERS: 2,
  RULES: 3,
  CONSUMED_DATA: 4,
  GENERATED_DATA: 5,
  TRADE_SUMMARY: 6,
  DATA_PREPARED: 7,
  DATA_RENDERED: 8,
};

/**
 * Checklist items in the order they appear in the checklist.
 * Each item has an order for display and a stageKey for parallel stage tracking.
 * @type {{order: number, stageKey: string}[]}
 */
const CHECKLIST_ITEMS = Object.freeze([
  { order: CHECKLIST_STATES.PORTFOLIO, stageKey: StageKeys.PORTFOLIO },
  { order: CHECKLIST_STATES.METERS, stageKey: StageKeys.METERS },
  { order: CHECKLIST_STATES.RULES, stageKey: StageKeys.RULES },
  { order: CHECKLIST_STATES.CONSUMED_DATA, stageKey: StageKeys.CONSUMED_DATA },
  { order: CHECKLIST_STATES.GENERATED_DATA, stageKey: StageKeys.GENERATED_DATA },
  { order: CHECKLIST_STATES.TRADE_SUMMARY, stageKey: StageKeys.TRADE_SUMMARY },
  { order: CHECKLIST_STATES.DATA_PREPARED, stageKey: StageKeys.DATA_PREPARED },
  { order: CHECKLIST_STATES.DATA_RENDERED, stageKey: StageKeys.DATA_RENDERED },
]);

const MIN_PROGRESS = 5;
const MAX_PROGRESS = 100;

/**
 * Calculates progress percentage based on completed stages.
 * Sums the weights from STAGE_PROGRESS for all completed stages.
 * Progress is clamped between MIN_PROGRESS (5%) and MAX_PROGRESS (100%).
 * @param {string[]} completedStages - Array of completed stage keys.
 * @returns {number} Progress percentage (5-100).
 */
export const calculateProgress = (completedStages = []) => {
  const completedProgress = completedStages.reduce(
    (sum, stageKey) => sum + (STAGE_PROGRESS[stageKey] || 0),
    0,
  );

  return Math.max(MIN_PROGRESS, Math.min(completedProgress, MAX_PROGRESS));
};

/**
 * Single source of truth for state metadata.
 * Maps each loading state to its checklist completion level.
 *
 * The `completedUpTo` property represents the highest checklist item order that should
 * be marked as COMPLETE when that loading state is active.
 * @type {{[key:string]: {completedUpTo: number}}}
 */
export const STATE_METADATA = Object.freeze({
  [PORTFOLIO_SHOW_LOADING_STATE_IDLE]: { completedUpTo: 0 },
  [PORTFOLIO_SHOW_LOADING_STATE_LOADING_PORTFOLIO]: { completedUpTo: 0 },
  [PORTFOLIO_SHOW_LOADING_STATE_LOADING_METERS]: {
    completedUpTo: CHECKLIST_STATES.PORTFOLIO,
  },
  [PORTFOLIO_SHOW_LOADING_STATE_METERS_LOADED]: {
    completedUpTo: CHECKLIST_STATES.METERS,
  },
  [PORTFOLIO_SHOW_LOADING_STATE_LOADING_RULES]: {
    completedUpTo: CHECKLIST_STATES.METERS,
  },
  [PORTFOLIO_SHOW_LOADING_STATE_RULES_LOADED]: {
    completedUpTo: CHECKLIST_STATES.RULES,
  },
  [PORTFOLIO_SHOW_LOADING_STATE_LOADING_CONSUMED]: {
    completedUpTo: CHECKLIST_STATES.RULES,
  },
  [PORTFOLIO_SHOW_LOADING_STATE_LOADING_GENERATED]: {
    completedUpTo: CHECKLIST_STATES.CONSUMED_DATA,
  },
  [PORTFOLIO_SHOW_LOADING_STATE_LOADING_TRADE_SUMMARY]: {
    completedUpTo: CHECKLIST_STATES.GENERATED_DATA,
  },
  [PORTFOLIO_SHOW_LOADING_STATE_PREPARING_DATA]: {
    completedUpTo: CHECKLIST_STATES.TRADE_SUMMARY,
  },
  [PORTFOLIO_SHOW_LOADING_STATE_RENDERING]: {
    completedUpTo: CHECKLIST_STATES.DATA_PREPARED,
  },
  [PORTFOLIO_SHOW_LOADING_STATE_COMPLETE]: {
    completedUpTo: CHECKLIST_STATES.DATA_RENDERED,
  },
  [PORTFOLIO_SHOW_LOADING_STATE_ERROR]: { completedUpTo: 0 },
});

/**
 * Determines the checklist item state based on the stage's presence in
 * activeStages and completedStages arrays.
 * @param {string} stageKey - The stage key to check.
 * @param {string[]} activeStages - Currently active stages.
 * @param {string[]} completedStages - Completed stages.
 * @returns {string} One of CHECKLIST_ITEM_STATE_COMPLETE, CHECKLIST_ITEM_STATE_ACTIVE,
 * or CHECKLIST_ITEM_STATE_INCOMPLETE.
 */
export const getChecklistItemState = (stageKey, activeStages, completedStages) => {
  if (completedStages.includes(stageKey)) {
    return CHECKLIST_ITEM_STATE_COMPLETE;
  }
  if (activeStages.includes(stageKey)) {
    return CHECKLIST_ITEM_STATE_ACTIVE;
  }
  return CHECKLIST_ITEM_STATE_INCOMPLETE;
};

/**
 * Formats the appropriate label for a loading stage based on its order and completion status.
 * @param {object} params - The parameters object
 * @param {object} params.intl - The react-intl intl object
 * @param {number} params.order - The stage order (1-8)
 * @param {string[]} params.completedStages - Array of completed stage keys
 * @param {string} params.stageKey - The stage key for this item
 * @param {string} params.currentState - The current loading state string
 * @param {object} params.metadata - The metadata object containing counts
 * @returns {string|React.ReactElement} The label for the specified stage.
 */
const formatStageLabel = ({
  intl,
  order,
  completedStages,
  stageKey,
  currentState,
  metadata,
}) => {
  const {
    metersCount = 0, rulesCount = 0, batchesLoaded = 0, batchesTotal = 0,
  } = metadata || {};
  const completedStagesArr = completedStages || [];
  const isCompleted = completedStagesArr.includes(stageKey);

  switch (order) {
    case CHECKLIST_STATES.PORTFOLIO: {
      return isCompleted
        ? intl.formatMessage({
          id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.portfolio_loaded.label',
          defaultMessage: 'Portfolio loaded',
        })
        : intl.formatMessage({
          id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.loading_portfolio.label',
          defaultMessage: 'Loading portfolio',
        });
    }

    case CHECKLIST_STATES.METERS: {
      const isLoadingMetersWithCount = (
        currentState === PortfolioShowLoadingStates.LOADING_METERS
        || currentState === PortfolioShowLoadingStates.METERS_LOADED
      ) && metersCount > 0;

      if (isCompleted || isLoadingMetersWithCount) {
        return (
          <span>
            {intl.formatMessage(
              {
                id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.meters_loaded.label',
                defaultMessage: '{count, plural, one {<strong>#</strong> meter} other {<strong>#</strong> meters}} loaded',
              },
              { count: metersCount, strong: (chunks) => <strong>{chunks}</strong> },
            )}
          </span>
        );
      }

      return intl.formatMessage({
        id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.loading_meters.label',
        defaultMessage: 'Loading meters',
      });
    }

    case CHECKLIST_STATES.RULES: {
      const isLoadingRulesWithCount = (
        currentState === PortfolioShowLoadingStates.LOADING_RULES
        || currentState === PortfolioShowLoadingStates.RULES_LOADED
      ) && rulesCount > 0;

      if (isCompleted || isLoadingRulesWithCount) {
        return (
          <span>
            {intl.formatMessage(
              {
                id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.rules_loaded.label',
                defaultMessage: '{count, plural, one {<strong>#</strong> trade rule} other {<strong>#</strong> trade rules}} loaded',
              },
              { count: rulesCount, strong: (chunks) => <strong>{chunks}</strong> },
            )}
          </span>
        );
      }
      return intl.formatMessage({
        id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.loading_rules.label',
        defaultMessage: 'Loading trade rules',
      });
    }

    case CHECKLIST_STATES.CONSUMED_DATA: {
      return isCompleted
        ? intl.formatMessage({
          id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.consumed_data_loaded.label',
          defaultMessage: 'Consumed meter data loaded',
        })
        : intl.formatMessage({
          id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.loading_consumed_data.label',
          defaultMessage: 'Loading consumed meter data',
        });
    }

    case CHECKLIST_STATES.GENERATED_DATA: {
      return isCompleted
        ? intl.formatMessage({
          id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.generated_data_loaded.label',
          defaultMessage: 'Generated meter data loaded',
        })
        : intl.formatMessage({
          id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.loading_generated_data.label',
          defaultMessage: 'Loading generated meter data',
        });
    }

    case CHECKLIST_STATES.TRADE_SUMMARY: {
      // Stage 5 now runs in parallel with Stages 2–4, so trade summary progress
      // may arrive before the reducer transitions to LOADING_TRADE_SUMMARY.
      // Show progress whenever batchesTotal is set (indicating Stage 5 has started).
      const isLoadingTradeSummary = (
        currentState === PortfolioShowLoadingStates.LOADING_TRADE_SUMMARY
        || batchesTotal > 0
      );

      if (isCompleted) {
        return (
          <span>
            {intl.formatMessage(
              {
                id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.trade_summary_loaded.label',
                defaultMessage: '{count, plural, one {<strong>#</strong> trade summary} other {<strong>#</strong> trade summaries}} loaded',
              },
              { count: batchesTotal, strong: (chunks) => <strong>{chunks}</strong> },
            )}
          </span>
        );
      }

      if (isLoadingTradeSummary && batchesTotal > 0) {
        return intl.formatMessage(
          {
            id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.loading_trade_summary_progress.label',
            defaultMessage: 'Loading {batchTotal, plural, one {trade summary} other {trade summaries}} ({batchLoaded} of {batchTotal})',
          },
          { batchLoaded: batchesLoaded, batchTotal: batchesTotal },
        );
      }

      return intl.formatMessage({
        id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.loading_trade_summary.label',
        defaultMessage: 'Loading trade summaries',
      });
    }

    case CHECKLIST_STATES.DATA_PREPARED: {
      return isCompleted
        ? intl.formatMessage({
          id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.data_prepared.label',
          defaultMessage: 'Data prepared',
        })
        : intl.formatMessage({
          id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.preparing_data.label',
          defaultMessage: 'Preparing data',
        });
    }

    case CHECKLIST_STATES.DATA_RENDERED: {
      return isCompleted
        ? intl.formatMessage({
          id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.data_rendered.label',
          defaultMessage: 'Data rendered',
        })
        : intl.formatMessage({
          id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.checklist.rendering_data.label',
          defaultMessage: 'Rendering data',
        });
    }

    default:
      return '';
  }
};

/**
 * Returns a checklist-style progress label showing all loading stages with dynamic messages.
 * @param {object} params - The parameters object
 * @param {object} params.intl - The react-intl intl object
 * @param {string} params.currentState - The current loading state
 * @param {object} params.metadata - The metadata object containing counts for each stage
 * @param {string[]} params.activeStages - Currently active stage keys
 * @param {string[]} params.completedStages - Completed stage keys
 * @returns {React.ReactElement} A JSX element containing the checklist
 */
const displayChecklistProgress = ({
  intl, currentState, metadata, activeStages = [], completedStages = [],
}) => (
  <div>
    {CHECKLIST_ITEMS.map((item) => {
      const itemState = getChecklistItemState(item.stageKey, activeStages, completedStages);
      const label = formatStageLabel({
        intl,
        order: item.order,
        completedStages,
        stageKey: item.stageKey,
        currentState,
        metadata,
      });
      return (
        <ChecklistItem key={item.order} state={itemState} label={label} />
      );
    })}
  </div>
);

/**
 * Generates loading overlay configuration based on the current loading state.
 * This function maps the PortfolioShow loading state machine's current state to
 * the appropriate overlay configuration.
 * @param {object} params - The parameters object.
 * @param {object} params.intl - The react-intl intl object.
 * @param {object} params.datespan - Object with start and finish DateTime.
 * @param {string} params.timezone - Timezone for date formatting.
 * @param {object} params.loadingState - The current loading state from the reducer.
 * @param {Function} [params.onCancelFunc] - Optional callback to cancel loading.
 * @param {boolean} [params.isRetrying] - Whether a retry is currently in flight. When
 * true and the reducer sits in a terminal state, the overlay is forced to render using
 * the LOADING_PORTFOLIO state so it stays mounted across the retry handshake.
 * @returns {{ loadingOverlayProps: object|null }} Overlay configuration object.
 */
export const buildLoadingOverlayState = ({
  intl,
  datespan,
  timezone,
  loadingState,
  onCancelFunc,
  isRetrying = false,
}) => {
  if (!loadingState || !intl || !datespan) {
    return { loadingOverlayProps: null };
  }

  const {
    state, metadata, activeStages = [], completedStages = [],
  } = loadingState;

  // During a retry the reducer can briefly return to a terminal state
  // (CANCELLED/ERROR) while the replay is dispatched. Treat the overlay as if
  // the portfolio load has restarted so it stays mounted across that frame,
  // keeping the LoadingOverlay and Placeholder skeleton in sync.
  const isTerminalState = state === PortfolioShowLoadingStates.IDLE
    || state === PortfolioShowLoadingStates.ERROR
    || state === PortfolioShowLoadingStates.CANCELLED;
  const effectiveState = isRetrying && isTerminalState
    ? PortfolioShowLoadingStates.LOADING_PORTFOLIO
    : state;

  const headerLabel = (
    <FormattedMessage
      id="portfolio.portfolio_show.helpers.build_loading_overlay_state.header.label"
      defaultMessage="{calenderIcon} Applying new date range"
      values={{
        calenderIcon: <FontAwesomeIcon icon={faCalendar} />,
      }}
    />
  );

  const bodyLabel = intl.formatMessage({
    id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.body.message',
    defaultMessage: 'Updating to show {dateRange}:',
  }, {
    dateRange: buildLocalizedDateRangeLabel(
      intl,
      datespan.start,
      datespan.finish,
      timezone,
    ),
  });

  const progressLabel = displayChecklistProgress({
    intl,
    currentState: effectiveState,
    metadata,
    activeStages,
    completedStages,
  });

  switch (effectiveState) {
    case PortfolioShowLoadingStates.IDLE:
    case PortfolioShowLoadingStates.ERROR:
    case PortfolioShowLoadingStates.CANCELLED:
      return { loadingOverlayProps: null };
    case PortfolioShowLoadingStates.LOADING_PORTFOLIO:
    case PortfolioShowLoadingStates.LOADING_METERS:
    case PortfolioShowLoadingStates.METERS_LOADED:
    case PortfolioShowLoadingStates.LOADING_RULES:
    case PortfolioShowLoadingStates.RULES_LOADED:
    case PortfolioShowLoadingStates.LOADING_CONSUMED:
    case PortfolioShowLoadingStates.LOADING_GENERATED:
    case PortfolioShowLoadingStates.LOADING_TRADE_SUMMARY:
    case PortfolioShowLoadingStates.PREPARING_DATA:
    case PortfolioShowLoadingStates.RENDERING:
    case PortfolioShowLoadingStates.COMPLETE:
      return {
        loadingOverlayProps: {
          headerLabel,
          bodyLabel,
          progressLabel,
          progressBarValue: calculateProgress(completedStages),
          updateProgress: () => {},
          onCancelFunc: onCancelFunc || null,
          cancelLabel: onCancelFunc
            ? intl.formatMessage({
              id: 'portfolio.portfolio_show.helpers.build_loading_overlay_state.cancel.label',
              defaultMessage: 'Cancel',
            })
            : null,
        },
      };
    default:
      return { loadingOverlayProps: null };
  }
};

export default buildLoadingOverlayState;
